import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import { wrapFraction } from "@aprao/common/src/math/wrapFraction.js";
import type { WithProjectionPeriods } from "@aprao/common/src/shapes/common.js";
import { makeAutoObservable } from "mobx";
import { mapForNullable } from "option-t/nullable";

import type {
  LandTaxCalculationType,
  ShapeLandTax,
  SiteUseClass,
} from "../shapes/ShapeSitePurchase.js";
import { WithProjectionPeriodsDelegate } from "./delegates/WithProjectionPeriodsDelegate.js";
import { relativesSetFacadeWithAbsolute } from "./RelativesSetFacade.js";
import { bandByUseClasses, calculateStampDuty } from "./stampDutyUtils.js";

export class LandTaxFacade {
  readonly $model;

  readonly _providers;

  constructor(
    model: ShapeLandTax & WithProjectionPeriods,
    providers: {
      pricePurchase: () => BigFraction | null;
    },
  ) {
    makeAutoObservable(this);

    this.$model = model;
    this._providers = providers;
  }

  get useClassCurrent() {
    return this.$model.useClassCurrent;
  }

  get calculationType() {
    return this.$model.calculationType;
  }

  get surcharge() {
    return wrapFraction(this.$model.surcharge);
  }

  get landTax() {
    if (this.calculationType === "manual") {
      // an escape hatch when the user needs to override land tax calculations
      return this.definedAmount.relativesSet.getRelative("absolute");
    } else {
      return mapForNullable(this._providers.pricePurchase(), (price) =>
        calculateStampDuty(
          price,
          bandByUseClasses[this.useClassCurrent],
          // non resi. can't include a surcharge
          this.useClassCurrent === "NonResidential" ? null : this.surcharge,
        ),
      );
    }
  }

  get definedAmount() {
    return relativesSetFacadeWithAbsolute(this.$model.definedAmount, {
      ratioPrice: this._providers.pricePurchase,
    });
  }

  get projectionPeriods() {
    return new WithProjectionPeriodsDelegate(this.$model);
  }

  set useClassCurrent(useClassCurrent: SiteUseClass) {
    this.$model.useClassCurrent = useClassCurrent;
  }

  set calculationType(type: LandTaxCalculationType) {
    this.$model.calculationType = type;
  }

  set surcharge(surcharge: BigFraction | null) {
    this.$model.surcharge = surcharge?.toFraction() ?? null;
  }
}
